import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import apiClient from '../services/apiClient';

const PermissionContext = createContext();

/**
 * PermissionProvider - Provides view-based permission checking for the application
 * 
 * This provider loads user permissions from the backend and provides methods
 * to check permissions throughout the application.
 */
export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState({});
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { user, isAuthenticated } = useSelector(state => state.auth);

  /**
   * Load permissions from the backend
   */
  const loadPermissions = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setPermissions({});
      setRoles([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Try to get permissions from the /permissions/me endpoint
      const response = await apiClient.get('/permissions/me');
      
      if (response.data.success) {
        setPermissions(response.data.data.permissions || {});
        setRoles(response.data.data.roles || []);
      }
    } catch (err) {
      console.error('Failed to load permissions:', err);
      
      // If 401, token might not be persisted yet — retry once after short delay
      if (err.response?.status === 401) {
        try {
          await new Promise(resolve => setTimeout(resolve, 500));
          const retryResponse = await apiClient.get('/permissions/me');
          if (retryResponse.data.success) {
            setPermissions(retryResponse.data.data.permissions || {});
            setRoles(retryResponse.data.data.roles || []);
            setLoading(false);
            return;
          }
        } catch (retryErr) {
          console.error('Retry also failed:', retryErr);
        }
      }
      
      setError(err.message || 'Failed to load permissions');
      
      // Fallback: try to get from user object (from login response)
      if (user?.viewPermissions) {
        console.log('Using permissions from user object');
        setPermissions(user.viewPermissions);
      } else {
        setPermissions({});
      }
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  /**
   * Load permissions when user changes
   */
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  /**
   * Check if user has a specific permission
   * Supports permission inheritance from parent views
   * 
   * @param {string} viewCode - View code (e.g., 'groups.detail.enrolledStudents')
   * @param {string} actionCode - Action code (e.g., 'add')
   * @returns {boolean} True if user has permission
   */
  const hasPermission = useCallback((viewCode, actionCode) => {
    if (!viewCode || !actionCode) return false;
    
    // Check direct permission
    if (permissions[viewCode]?.[actionCode]) {
      return true;
    }

    // Check inherited permission from parent views
    const parts = viewCode.split('.');
    while (parts.length > 1) {
      parts.pop();
      const parentViewCode = parts.join('.');
      
      if (permissions[parentViewCode]?.[actionCode]) {
        return true;
      }
    }

    return false;
  }, [permissions]);

  /**
   * Check if user has any of the specified permissions
   * 
   * @param {string} viewCode - View code
   * @param {string[]} actionCodes - Array of action codes
   * @returns {boolean} True if user has at least one permission
   */
  const hasAnyPermission = useCallback((viewCode, actionCodes) => {
    if (!Array.isArray(actionCodes)) return false;
    return actionCodes.some(action => hasPermission(viewCode, action));
  }, [hasPermission]);

  /**
   * Check if user has all of the specified permissions
   * 
   * @param {string} viewCode - View code
   * @param {string[]} actionCodes - Array of action codes
   * @returns {boolean} True if user has all permissions
   */
  const hasAllPermissions = useCallback((viewCode, actionCodes) => {
    if (!Array.isArray(actionCodes)) return false;
    return actionCodes.every(action => hasPermission(viewCode, action));
  }, [hasPermission]);

  /**
   * Get all permissions for a specific view
   * 
   * @param {string} viewCode - View code
   * @returns {Object} Object with action codes as keys and boolean values
   */
  const getViewPermissions = useCallback((viewCode) => {
    return permissions[viewCode] || {};
  }, [permissions]);

  /**
   * Check if user has a specific role
   * 
   * @param {string} roleName - Role name
   * @returns {boolean} True if user has the role
   */
  const hasRole = useCallback((roleName) => {
    return roles.some(role => role.name === roleName || role.code === roleName);
  }, [roles]);

  /**
   * Check if user has any of the specified roles
   * 
   * @param {string[]} roleNames - Array of role names
   * @returns {boolean} True if user has at least one role
   */
  const hasAnyRole = useCallback((roleNames) => {
    if (!Array.isArray(roleNames)) return false;
    return roleNames.some(roleName => hasRole(roleName));
  }, [hasRole]);

  /**
   * Refresh permissions from the backend
   */
  const refresh = useCallback(() => {
    return loadPermissions();
  }, [loadPermissions]);

  const value = {
    permissions,
    roles,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getViewPermissions,
    hasRole,
    hasAnyRole,
    refresh
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

/**
 * Hook to use permission context
 * Must be used within PermissionProvider
 */
export const usePermission = () => {
  const context = useContext(PermissionContext);
  
  if (!context) {
    throw new Error('usePermission must be used within PermissionProvider');
  }
  
  return context;
};

export default PermissionContext;
